import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { apiClient } from '@/lib/api'
import { getReturnUrl } from '@/lib/auth/return-url'
import { useAuthStore } from '@/stores/auth-store'

interface Subscriber {
  name: string
  active: boolean
  config: string
  description: string
  display_name: string
  gp: string
  password: string
}

function SystemSelectionLoading() {
  return (
    <div className="flex justify-center py-8">
      <LoadingSpinner size="lg" />
      <p className="ml-3 text-gray-300">Loading systems...</p>
    </div>
  )
}

function SystemSelector({
  subscribers,
  selectedSystem,
  onSelect,
  onContinue,
  onCancel,
}: {
  subscribers: Subscriber[]
  selectedSystem: string
  onSelect: (value: string) => void
  onContinue: () => void
  onCancel: () => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="system" className="block text-sm font-medium text-gray-300 mb-2">
          Choose a system
        </label>
        <select
          id="system"
          value={selectedSystem}
          onChange={(e) => {
            onSelect(e.target.value)
          }}
          className={
            // eslint-disable-next-line no-secrets/no-secrets
            "block w-full pl-3.5 pr-14 py-2.5 text-sm border border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-700 text-white transition-all appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%239ca3af%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem] bg-[right_0.75rem_center] bg-no-repeat"
          }
        >
          <option value="">Select a system...</option>
          {subscribers.map((system) => (
            <option key={system.name} value={system.name}>
              {system.display_name || system.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-3">
        <Button variant="secondary" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button onClick={onContinue} disabled={!selectedSystem} className="flex-1">
          Continue
        </Button>
      </div>
    </div>
  )
}

const BACKGROUND_STYLE = {
  backgroundImage: 'url(/images/login-background.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
} as const

function sortSubscribers(subscribers: Subscriber[]): Subscriber[] {
  return [...subscribers].sort((a, b) => {
    const nameA = (a.display_name || a.name).toLowerCase()
    const nameB = (b.display_name || b.name).toLowerCase()
    return nameA.localeCompare(nameB)
  })
}

function SystemSelectionCard({
  user,
  isLoading,
  availableSubscribers,
  selectedSystem,
  onSelect,
  onContinue,
  onCancel,
}: {
  user: { first_name: string; last_name: string }
  isLoading: boolean
  availableSubscribers: Subscriber[]
  selectedSystem: string
  onSelect: (value: string) => void
  onContinue: () => void
  onCancel: () => void
}) {
  return (
    <div className="bg-gray-800 rounded-lg shadow-xl p-8">
      <div className="flex justify-center mb-6">
        <img
          src="/images/CloudSent-POS-logo-white-sm.png"
          alt="CloudSent POS"
          className="h-16 w-auto shrink-0 object-contain"
        />
      </div>

      <div className="text-center mb-6">
        <p className="text-sm text-gray-300 mb-4">
          Welcome, {user.first_name} {user.last_name}. You have access to multiple systems. Please
          choose a system to continue. You can change systems at any time.
        </p>
      </div>

      {isLoading ? (
        <SystemSelectionLoading />
      ) : (
        <SystemSelector
          subscribers={availableSubscribers}
          selectedSystem={selectedSystem}
          onSelect={onSelect}
          onContinue={onContinue}
          onCancel={onCancel}
        />
      )}
    </div>
  )
}

export function SystemSelectionPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, selectSystem, logout } = useAuthStore()
  const returnUrl = getReturnUrl(location.state)
  const [selectedSystem, setSelectedSystem] = useState<string>('')
  const [availableSubscribers, setAvailableSubscribers] = useState<Subscriber[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true })
      return
    }

    if (!user.sys_op && !user.systems_all && (!user.systems || user.systems.length === 0)) {
      logout()
      navigate('/login', { replace: true })
      return
    }

    if (!user.sys_op && !user.systems_all && user.systems?.length === 1) {
      selectSystem(user.systems[0])
      navigate(returnUrl, { replace: true })
      return
    }

    void fetchSystems()
  }, [user, navigate, logout, selectSystem, returnUrl])

  const fetchSystems = async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.get<{ systems?: Subscriber[]; total: number }>(
        '/core/v5/systems'
      )
      setAvailableSubscribers(sortSubscribers(response.systems ?? []))
    } catch (error) {
      console.error('Failed to fetch systems:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSystemSelect = () => {
    if (!selectedSystem) return
    selectSystem(selectedSystem)
    navigate(returnUrl, { replace: true })
  }

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  if (!user) return null

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={BACKGROUND_STYLE}
    >
      <div className="absolute inset-0 bg-black/50" />

      <div className="relative max-w-md w-full">
        <SystemSelectionCard
          user={user}
          isLoading={isLoading}
          availableSubscribers={availableSubscribers}
          selectedSystem={selectedSystem}
          onSelect={setSelectedSystem}
          onContinue={handleSystemSelect}
          onCancel={handleLogout}
        />
      </div>
    </div>
  )
}
